import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import EventSocial from '../../components/common/EventSocial';
import ProfileCompleteness from '../../components/common/ProfileCompleteness';
import {
  apiRequest, eventDayLabel, eventStartLabel, eventZone, formatTime, hasEventTime, mapsLink,
} from '../../utils';
import './LabToday.css';

/* The Lab that is happening — from a day before the doors open until a week after they shut,
   on the screen a member actually opens.

   ★ WHY IT EXISTS. After the first live Lab: everything a participant needed on the day lived
   one click away on /public-event/:id — the room, the hours, the programme, and above all the
   album and the wall. People did not find them. The home said only "you have a QR" and, if the
   host had attached one, "you still owe an answer".

   ★ TWO FACES, and `phase` (server-side, routes/bravos._lab_phase) is the switch:
     before / running — the day itself: hours, room, programme, both codes, the questions owed,
                        and the wall;
     ended            — what the Lab left behind: the hosts, the photographs, the wall. No door
                        code (the door is shut), no networking code (the mint refuses it outside
                        its own ±1 day, so the button would be a promise the next screen breaks),
                        no room and no programme.

   ★ The phase is NOT computed here. `date`/`end_date` are stored UTC, and a browser comparing
   them to its own clock calls a Dubai Lab over while people are still in the room. The times
   below are RENDERED in the event's zone for the same reason — never the viewer's.

   ★ It renders the cheap half from what the home already loaded, and upgrades. The calendar row
   in the home payload already carries the day, the hours, the zone and the host names; only the
   room description, the maps and the programme need the second request. So the card is complete
   the moment the home paints, and grows a programme a beat later — rather than making the one
   screen somebody opens while standing up wait on a 16-query route. */

const LabToday = ({ lab, calendarRow, preSurveys = [], postSurveys = [],
                    onShowMeetQr }) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);

  const labId = lab && lab.id;
  const ended = !!lab && lab.phase === 'ended';

  useEffect(() => {
    // Nothing to fetch once it is over: the room and the programme are not drawn, and the
    // hosts ride along with the album's own payload.
    if (!labId || ended) { setDetail(null); return undefined; }
    let cancelled = false;
    apiRequest('GET', `/public-event/${labId}`)
      .then((d) => { if (!cancelled) setDetail((d && d.event) || null); })
      .catch(() => { /* the card stands on the home payload alone */ });
    return () => { cancelled = true; };
  }, [labId, ended]);

  if (!lab) return null;

  // What we know without the second request, and what it adds.
  const row = calendarRow || {};
  const when = detail || row;
  const tz = eventZone(when);
  const dayLabel = eventDayLabel(when);
  const startLabel = eventStartLabel(when);
  const endLabel = hasEventTime(when.end_date) && formatTime(when.end_date, tz) !== startLabel
    ? formatTime(when.end_date, tz)
    : '';
  const timeLabel = startLabel && (endLabel ? `${startLabel}–${endLabel}` : startLabel);
  const place = (detail && detail.location_name) || row.location_name || lab.location_name;
  // location_embed is the <iframe> markup, not a URL — see mapsLink.
  const directions = detail && detail.location_embed ? mapsLink(detail.location_embed, place) : null;
  const hosts = (detail && detail.hosts) || [];
  const planning = Object.entries((detail && detail.planning_by_day) || {});

  return (
    <>
      <Card>
        <Title my="1" className="mt-2">
          <Icon name="calendar-star" theme="primary" className="me-2" />
          {lab.name}
        </Title>
        {ended && <p className="lab-today__past">{t('labEndedNote')}</p>}

        {/* ---- the practicals: only while it is still ahead or on ---- */}
        {!ended && (dayLabel || timeLabel || place) && (
          <div className="lab-today__facts">
            {dayLabel && (
              <span className="lab-today__fact">
                <Icon name="calendar" /> {dayLabel}{timeLabel ? ` · ${timeLabel}` : ''}
              </span>
            )}
            {place && (
              <span className="lab-today__fact">
                <Icon name="marker" /> {place}
              </span>
            )}
          </div>
        )}
        {!ended && detail && detail.location_desc && (
          <p className="lab-today__where">{detail.location_desc}</p>
        )}
        {!ended && directions && (
          <a
            className="bravos-submit bravos-submit--ghost"
            href={directions}
            target="_blank"
            rel="noreferrer"
          >
            <Icon name="marker" /> {t('labDirections')}
          </a>
        )}

        {/* ---- who is running it. Stays after the event: the faces are the point ---- */}
        {hosts.length > 0 && (
          <div className="lab-today__hosts">
            {hosts.map((h) => (
              <Link key={h.partner_id} className="lab-today__host" to={`/host/${h.partner_id}`}>
                {h.photo_url
                  ? <img src={h.photo_url} alt="" />
                  : <span className="lab-today__host-blank">{(h.name || '?').charAt(0)}</span>}
                <span className="lab-today__host-name">{h.name}</span>
              </Link>
            ))}
          </div>
        )}

        {/* ---- the two codes. They do DIFFERENT things and used to look identical ---- */}
        {!ended && (lab.qr_token || lab.networking) && (
          <div className="lab-today__codes">
            {/* The door code is a card, open by default, never behind a tap: it is the thing
                somebody is holding their phone up for. It disappears the moment they are in. */}
            {lab.qr_token && !lab.attended && (
              <div className="lab-today__door">
                <div className="lab-today__door-title">{t('labDoorTitle')}</div>
                <div className="lab-today__door-sub">{t('labDoorSub')}</div>
                <div className="lab-today__door-qr">
                  <QRCodeSVG value={lab.qr_token} size={180} level="M" includeMargin={false} />
                </div>
              </div>
            )}
            {lab.qr_token && lab.attended && (
              <p className="lab-today__door-done">
                <Icon name="check" theme="primary" /> {t('labDoorDone')}
              </p>
            )}
            {/* The networking code stays a secondary button. Its own sheet already names the
                fields it hands over; the line here says the one thing that was missing — that
                it is a DIFFERENT code from the one above, which is why holding it up at the
                scanner answered invalidQrCode. */}
            {lab.networking && (
              <div className="lab-today__meet">
                <div className="lab-today__meet-row">
                  <button type="button" className="bravos-submit" onClick={onShowMeetQr}>
                    <Icon name="user-add" /> {t('labMeetTitle')}
                  </button>
                  {/* What that code hands over is exactly what a half-filled profile does not
                      have, so the nudge sits beside it and not somewhere else on the page. Same
                      component as the ring in the header, reading the same call — a pill that
                      did its own sum would sooner or later say a different number three
                      centimetres lower. It draws nothing at 100%. */}
                  <ProfileCompleteness
                    variant="pill"
                    className="bravos-submit bravos-submit--ghost"
                  />
                </div>
                <p className="lab-today__meet-sub">{t('labMeetSub')}</p>
              </div>
            )}
          </div>
        )}

        {/* ---- the questions the host is still waiting on ---- */}
        {!ended && preSurveys.map((s) => (
          <Link key={s.id} className="bravos-submit bravos-submit--ghost" to={s.path}>
            <Icon name="comment-question" /> {s.name} <Icon name="angle-right" />
          </Link>
        ))}
        {!ended && preSurveys.length > 0 && (
          <p className="bravos-cal__notice">{t('preSurveyNote')}</p>
        )}

        {/* ---- the programme ---- */}
        {!ended && planning.length > 0 && (
          <div className="lab-today__plan">
            <div className="lab-today__plan-title">{t('labProgramme')}</div>
            {planning.map(([dayIso, items]) => (
              <div key={dayIso}>
                {(items || []).map((a, i) => (
                  <div className="lab-today__slot" key={`${dayIso}-${i}`}>
                    {/* Planning stamps are already event-LOCAL wall clock (Planning.start_time),
                        so they are read literally — putting them through Date() would convert a
                        time that has already been converted. */}
                    <span className="lab-today__slot-time">
                      {String(a.start_time || '').slice(11, 16)}
                    </span>
                    <span className="lab-today__slot-name">{a.name || ''}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {/* ---- "how was it" ----
            ★ Last in the card ON PURPOSE, which puts it directly above the photographs. In the
            days after a Lab the album is what brings somebody back to this card at all, so the
            one thing still owed has to sit in the path they are already on — not above the
            practicals they no longer need, and not below a wall they scroll forever.
            The server sends it only from the moment the doors opened, and only while THIS
            reader has not answered, so it empties itself rather than nagging. */}
        {postSurveys.length > 0 && (
          <div className="lab-today__after">
            {postSurveys.map((s) => (
              <Link key={s.id} className="bravos-submit" to={s.path}>
                <Icon name="comment" /> {s.name} <Icon name="angle-right" />
              </Link>
            ))}
            <p className="bravos-cal__notice">{t('postSurveyNote')}</p>
          </div>
        )}
      </Card>

      {/* ---- the recap, the photographs and the wall, on the home instead of one click away.
              EventSocial draws nothing at all until the server says yes (Bravos only, and only
              for somebody on the roster), so this is safe to mount unconditionally. Its gate
              carries no date window, which is what lets the album stay open for the days after
              the Lab when the photographs actually arrive.

              ★ IN A CARD, exactly as HostEvent mounts it. `.es` brings no gutter of its own by
              design — it takes the one from whatever holds it — so hung bare on the member home
              all three blocks ran edge to edge while every other card on the page sat inside
              Card's mx-3. That reads as broken rather than as full-bleed.
              No title on the card: EventSocial already heads each of its three blocks, and a
              fourth heading above them would only name the other three. ---- */}
      <Card className="lab-today__social">
        <EventSocial eventId={lab.id} />
      </Card>
    </>
  );
};

export default LabToday;
